import React, { useState } from "react";
import {
    Building2,
    Settings,
    Shield,
    Activity,
    Users,
    Heart,
    CheckCircle,
    XCircle,
    ExternalLink,
    ChevronRight,
    MoreVertical,
    Power,
    Zap,
    Bell,
    Key,
    RefreshCw,
    Info,
    Star,
    Calendar,
    DollarSign,
    User,
    Globe,
    Database
} from "lucide-react";

const XFinanceConnect = () => {
    const [providers, setProviders] = useState([
        {
            id: "miguelmx",
            name: "MiguelMX",
            description: "Tu tranquilidad financiera empieza aquí.",
            category: "Contabilidad",
            logo: "https://static.wixstatic.com/media/755cdc_fba32a57a6554eb9b4205d19bf8f419e~mv2.png/v1/fill/w_286,h_54,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/Logo%20verde.png",
            status: "connected",
            lastSync: "2025-08-19T10:30:00",
            features: ["Sincronización de facturas", "Reportes financieros", "Gestión de clientes"],
            plan: "Pro",
            monthlyFee: 0,
            icon: Database,
            color: "bg-green-600"
        },
        {
            id: "gnp-seguros",
            name: "GNP Seguros",
            description: "Seguros corporativos y personales",
            category: "Seguros",
            logo: "https://kayum.mx/wp-content/uploads/2019/09/logo-GNP.jpeg",
            status: "disconnected",
            lastSync: null,
            features: ["Pólizas corporativas", "Seguros de vida", "Seguros de auto"],
            plan: "Empresarial",
            monthlyFee: 2500,
            icon: Shield,
            color: "bg-blue-600"
        },
        {
            id: "sofia-salud",
            name: "Sofia Salud",
            description: "Seguros de salud para personal de empresa",
            category: "Salud",
            logo: "https://assets.website-files.com/5e1f4a5c04a2d590242d98a8/5e2745ccfe29f0dbeb364109_Logo_.png",
            status: "pending",
            lastSync: null,
            features: ["Seguros médicos", "Cobertura para empleados", "Telemedicina"],
            plan: "Corporativo",
            monthlyFee: 3200,
            icon: Heart,
            color: "bg-red-600"
        }
    ]);

    const [selectedProvider, setSelectedProvider] = useState(null);
    const [showConfigModal, setShowConfigModal] = useState(false);

    const handleToggleProvider = (providerId) => {
        setProviders(prev => prev.map(provider => {
            if (provider.id === providerId) {
                const newStatus = provider.status === "connected" ? "disconnected" : "connected";
                return {
                    ...provider,
                    status: newStatus,
                    lastSync: newStatus === "connected" ? new Date().toISOString() : null
                };
            }
            return provider;
        }));
    };

    const handleConfigureProvider = (provider) => {
        setSelectedProvider(provider);
        setShowConfigModal(true);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case "connected":
                return "text-green-600 bg-green-100";
            case "disconnected":
                return "text-slate-600 bg-slate-100";
            case "pending":
                return "text-orange-600 bg-orange-100";
            default:
                return "text-slate-600 bg-slate-100";
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case "connected":
                return <CheckCircle className="w-4 h-4" />;
            case "disconnected":
                return <XCircle className="w-4 h-4" />;
            case "pending":
                return <RefreshCw className="w-4 h-4" />;
            default:
                return <XCircle className="w-4 h-4" />;
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case "connected":
                return "Conectado";
            case "disconnected":
                return "Desconectado";
            case "pending":
                return "Pendiente";
            default:
                return "Desconocido";
        }
    };

    const connectedProviders = providers.filter(p => p.status === "connected");
    const totalMonthlyCost = providers
        .filter(p => p.status === "connected")
        .reduce((sum, p) => sum + p.monthlyFee, 0);

    const statsCards = [
        {
            title: "Proveedores Conectados",
            value: connectedProviders.length.toString(),
            subtitle: `de ${providers.length} disponibles`,
            icon: Zap,
            color: "bg-blue-600"
        },
        {
            title: "Costo Mensual",
            value: `$${totalMonthlyCost.toLocaleString("es-MX")}`,
            subtitle: "Servicios activos",
            icon: DollarSign,
            color: "bg-green-600"
        },
        {
            title: "Última Sincronización",
            value: connectedProviders.length > 0 ? "Hace 2 horas" : "N/A",
            subtitle: "Datos actualizados",
            icon: RefreshCw,
            color: "bg-purple-600"
        },
        {
            title: "Estado General",
            value: connectedProviders.length > 0 ? "Operativo" : "Inactivo",
            subtitle: "Sistema de conexiones",
            icon: Activity,
            color: connectedProviders.length > 0 ? "bg-green-600" : "bg-slate-600"
        }
    ];

    return (
        <div className="min-h-screen bg-slate-50">
            {/* Header */}
            <div className="bg-white border-b border-slate-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-3xl font-bold text-slate-900">XFinance Connect</h1>
                            <p className="text-slate-600 font-medium">
                                Gestiona tus conexiones con proveedores de servicios
                            </p>
                        </div>
                        <div className="flex items-center space-x-3">
                            <button
                                onClick={() => console.log("Navigate to dashboard")}
                                className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                            >
                                <Building2 className="w-4 h-4" />
                                <span className="font-medium">Dashboard</span>
                            </button>
                            <div className="w-10 h-10 bg-gradient-to-r from-blue-900 to-blue-800 rounded-lg flex items-center justify-center">
                                <span className="text-white font-bold text-lg">X</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    {statsCards.map((stat, index) => {
                        const Icon = stat.icon;
                        return (
                            <div key={index} className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <div className={`w-12 h-12 ${stat.color} rounded-lg flex items-center justify-center`}>
                                        <Icon className="w-6 h-6 text-white" />
                                    </div>
                                </div>
                                <div>
                                    <p className="text-2xl font-bold text-slate-900 mb-1">{stat.value}</p>
                                    <p className="text-slate-600 font-medium">{stat.title}</p>
                                    <p className="text-sm text-slate-500 mt-1">{stat.subtitle}</p>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Providers Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 mb-8">
                    {providers.map((provider) => {
                        const Icon = provider.icon;
                        return (
                            <div key={provider.id} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                                {/* Provider Header */}
                                <div className="p-6 border-b border-slate-100">
                                    <div className="flex items-start justify-between mb-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-12 h-12 bg-white rounded-lg border border-slate-200 flex items-center justify-center overflow-hidden">
                                                <img
                                                    src={provider.logo}
                                                    alt={provider.name}
                                                    className="w-10 h-6 object-contain"
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                        e.target.nextSibling.style.display = 'flex';
                                                    }}
                                                />
                                                <div className={`w-10 h-10 ${provider.color} rounded-lg items-center justify-center hidden`}>
                                                    <Icon className="w-5 h-5 text-white" />
                                                </div>
                                            </div>
                                            <div>
                                                <h3 className="text-lg font-bold text-slate-900">{provider.name}</h3>
                                                <p className="text-sm text-slate-500">{provider.category}</p>
                                            </div>
                                        </div>
                                        <button className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
                                            <MoreVertical className="w-4 h-4 text-slate-400" />
                                        </button>
                                    </div>

                                    <p className="text-slate-600 text-sm mb-4">{provider.description}</p>

                                    {/* Status */}
                                    <div className="flex items-center justify-between mb-4">
                                        <div className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(provider.status)}`}>
                                            {getStatusIcon(provider.status)}
                                            <span>{getStatusText(provider.status)}</span>
                                        </div>
                                        {provider.status === "connected" && provider.lastSync && (
                                            <div className="text-xs text-slate-500">
                                                Sync: {new Date(provider.lastSync).toLocaleTimeString("es-MX", {
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Provider Details */}
                                <div className="p-6">
                                    {/* Features */}
                                    <div className="mb-4">
                                        <h4 className="text-sm font-semibold text-slate-900 mb-2">Características</h4>
                                        <div className="space-y-1">
                                            {provider.features.map((feature, index) => (
                                                <div key={index} className="flex items-center gap-2 text-sm text-slate-600">
                                                    <div className="w-1.5 h-1.5 bg-slate-400 rounded-full"></div>
                                                    <span>{feature}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Plan & Cost */}
                                    <div className="flex items-center justify-between mb-6">
                                        <div>
                                            <p className="text-sm font-medium text-slate-900">Plan {provider.plan}</p>
                                            <p className="text-sm text-slate-500">
                                                {provider.monthlyFee > 0
                                                    ? `$${provider.monthlyFee.toLocaleString("es-MX")}/mes`
                                                    : "Gratuito"
                                                }
                                            </p>
                                        </div>
                                        <Star className="w-4 h-4 text-yellow-500" />
                                    </div>

                                    {/* Actions */}
                                    <div className="space-y-2">
                                        <button
                                            onClick={() => handleToggleProvider(provider.id)}
                                            className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${provider.status === "connected"
                                                ? "bg-red-100 text-red-700 hover:bg-red-200"
                                                : "bg-blue-600 text-white hover:bg-blue-700"
                                                }`}
                                        >
                                            <Power className="w-4 h-4" />
                                            {provider.status === "connected" ? "Desconectar" : "Conectar"}
                                        </button>

                                        <button
                                            onClick={() => handleConfigureProvider(provider)}
                                            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 hover:bg-slate-200 rounded-lg font-medium transition-colors"
                                        >
                                            <Settings className="w-4 h-4" />
                                            Configurar
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Quick Actions */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Connected Services Summary */}
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                        <h3 className="text-xl font-bold text-slate-900 mb-4">Servicios Conectados</h3>
                        {connectedProviders.length > 0 ? (
                            <div className="space-y-4">
                                {connectedProviders.map((provider) => (
                                    <div key={provider.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 bg-white rounded border border-slate-200 flex items-center justify-center overflow-hidden">
                                                <img
                                                    src={provider.logo}
                                                    alt={provider.name}
                                                    className="w-6 h-3 object-contain"
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                        e.target.nextSibling.style.display = 'flex';
                                                    }}
                                                />
                                                <div className={`w-6 h-6 ${provider.color} rounded items-center justify-center hidden`}>
                                                    <provider.icon className="w-3 h-3 text-white" />
                                                </div>
                                            </div>
                                            <div>
                                                <p className="font-medium text-slate-900">{provider.name}</p>
                                                <p className="text-sm text-slate-500">{provider.category}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                            <span className="text-sm text-green-600 font-medium">Activo</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-8">
                                <Globe className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                                <p className="text-slate-600 font-medium">No hay servicios conectados</p>
                                <p className="text-slate-500 text-sm">Conecta un proveedor para empezar</p>
                            </div>
                        )}
                    </div>

                    {/* System Status */}
                    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                        <h3 className="text-xl font-bold text-slate-900 mb-4">Estado del Sistema</h3>
                        <div className="space-y-4">
                            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                                <div className="flex items-center gap-3">
                                    <CheckCircle className="w-5 h-5 text-green-600" />
                                    <span className="font-medium text-slate-900">API Gateway</span>
                                </div>
                                <span className="text-sm text-green-600 font-medium">Operativo</span>
                            </div>

                            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                                <div className="flex items-center gap-3">
                                    <CheckCircle className="w-5 h-5 text-green-600" />
                                    <span className="font-medium text-slate-900">Sincronización</span>
                                </div>
                                <span className="text-sm text-green-600 font-medium">Activa</span>
                            </div>

                            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                                <div className="flex items-center gap-3">
                                    <Info className="w-5 h-5 text-blue-600" />
                                    <span className="font-medium text-slate-900">Próximo Mantenimiento</span>
                                </div>
                                <span className="text-sm text-blue-600 font-medium">25 Ago</span>
                            </div>
                        </div>

                        <div className="mt-6 pt-4 border-t border-slate-200">
                            <button className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg font-medium transition-colors">
                                <RefreshCw className="w-4 h-4" />
                                Sincronizar Todo
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Configuration Modal */}
            {showConfigModal && selectedProvider && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl max-w-md w-full p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-bold text-slate-900">
                                Configurar {selectedProvider.name}
                            </h3>
                            <button
                                onClick={() => setShowConfigModal(false)}
                                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                            >
                                <XCircle className="w-5 h-5 text-slate-400" />
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">
                                    Frecuencia de Sincronización
                                </label>
                                <select className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                                    <option>Cada hora</option>
                                    <option>Cada 6 horas</option>
                                    <option>Diariamente</option>
                                    <option>Semanalmente</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">
                                    API Key
                                </label>
                                <input
                                    type="password"
                                    placeholder="Ingresa tu API Key"
                                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>

                            <div className="flex items-center gap-3">
                                <input type="checkbox" id="notifications" className="rounded" />
                                <label htmlFor="notifications" className="text-sm text-slate-700">
                                    Recibir notificaciones de sincronización
                                </label>
                            </div>
                        </div>

                        <div className="flex gap-3 mt-6">
                            <button
                                onClick={() => setShowConfigModal(false)}
                                className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={() => setShowConfigModal(false)}
                                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                Guardar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default XFinanceConnect;